import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFlag } from '@innovid/feature-flags-react';
import { CreativePreviews } from "reducers/CreativePreviews";
import SelectedRow from "utils/columns/SelectedRow";
import { DataGrid, ProgressIndicator } from "rhombus-react";
import {
	DEFAULT_ROW_HEIGHT,
	MIN_GRID_HEIGHT
} from "utils/constants";
import { useGridScrollUtils } from "utils/DataGrid";
import { PreviewCreative, PreviewVersion } from "utils/Preview";
import { CREATIVE_PREVIEWS_COLUMNS } from "widgets/grid/columns/Previews";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import Measure from "react-measure";
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';

export default function Grid({ libraryId, sort="", isLoading }) {
	const dispatch = useDispatch()
		, [sortColumn, sortDirection] = sort.split(":")
		, { previews, currentSelection } = useSelector((state) => {
			return {
				previews: state.creativePreviews.items,
				currentSelection: state.selected.creativePreviews,
			};
		})
		;
	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);

	const creativePreviews = useMemo(() => {
			return previews.map((preview) => {
				let publicUrlCreative;
				let publicUrlVersions;
				if (preview.type === 'mockup') {
					if (preview.publicId) {
						publicUrlCreative = PreviewCreative.legacy.public(preview.publicId);
					}
				} else {
					const creatives = preview.creatives || [];
					const creative = creatives.find((creative) => {
						return PreviewCreative.public(creative, forceNewPreview).isLegacy ? creative : undefined;
					}) || creatives[0];
					publicUrlCreative = PreviewCreative.public({ ...creative, id: preview.publicId }, forceNewPreview).url;
					publicUrlVersions = PreviewVersion.public({ ...creative, id: preview.publicId }, forceNewPreview).url;
				}
				return {
					...preview,
					libraryId,
					publicUrlCreative,
					publicUrlVersions,
					isSelected: currentSelection.indexOf(preview.id) > -1,
				};
			});
		}, [libraryId, previews, currentSelection, forceNewPreview])
		, dataGridRef = useGridScrollUtils([creativePreviews])
		;

	const getRow = (rowID) => {
		if(rowID === -1){ return; } // -1 is the header row, and doesn't need to return data
		return creativePreviews[rowID];
	};

	useTooltipRebuild(creativePreviews);

	const dataGridProps = {
		columns: CREATIVE_PREVIEWS_COLUMNS,
		rowGetter: getRow,
		rowsCount: creativePreviews.length,
		rowRenderer: SelectedRow,
		rowHeight: DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(CreativePreviews.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<Measure>
			{(obj) => {
				let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
				return (
					<div className="measure-component" ref={obj.measureRef}>
						<ProgressIndicator isLoading={isLoading} />
						<div ref={dataGridRef}>
							<DataGrid
								{...dataGridProps}
								minHeight={gridHeight}
							/>
						</div>
					</div>
				);
			}}
		</Measure>
	);
}

Grid.displayName = "Grid";
